import { put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCoreOpenApiInfo } from 'CorePackage/MainModule/selectors/coreSelectors';
import { IKnmSearchFilter } from 'APIPackage/KnmSearchModule/interfaces/IKnmSearchFilter';
import { exportKnm, searchKNM } from 'APIPackage/KnmSearchModule/services/knmSearchApi';
import { IKnmSearchResponse } from 'APIPackage/KnmSearchModule/interfaces/IKnmSearchResponse';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { catalogGet, catalogKnmTypes248Get } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import {
  fetchErpSupervisionTypesErvkByFederalLaw,
  fetchKnm248SupervisionTypesErvkByFederalLaw,
  fetchKnmSupervisionTypesAll,
} from 'APIPackage/AdminModule/services/adminKnmSupervisionTypesApi';
import { IKnmSupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';
import { IKnmSupervisionType } from 'APIPackage/CatalogsModule/interfaces/IKnmSupervisionType';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  knmSearchLoadFile,
  knmSearchSetData,
  knmSearchSetKnmSupervisionTypesErvkOptionsAll,
  knmSearchSetKnmSupervisionTypesOptionsAll,
} from '../actions/knmSearchActions';
import { KnmSearchActionsEnum } from '../enums/KnmSearchActionsEnum';
import {
  getKnmSearchFilter,
  getKnmSearchKnms,
  getKnmSearchOrder,
  getKnmSearchPage,
  getKnmSearchSearchString,
} from '../selectors/knmSearchSelectors';
import { knmSearchGetFilters } from '../common/knmSearchGetFilters';
import { dictionariesKnmSubjectTypesGet } from '../../../CorePackage/DictionariesModule/actions/dictionariesActions';
import { ISupervisionType } from '../../../APIPackage/DictionariesModule/interfaces/ISupervisionType';
import { ISupervisionTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ISupervisionTypeSelectOption';

const PAGE_SIZE = 50;

export function* watchKnmSearchSaga() {
  yield takeEvery(KnmSearchActionsEnum.KNM_SEARCH_INIT, knmSearchInitSaga);
  yield takeEvery(KnmSearchActionsEnum.KNM_SEARCH_GET_DATA, knmSearchGetDataSaga);
  yield takeEvery(KnmSearchActionsEnum.KNM_SEARCH_GET_DATA_NEXT_PAGE, knmSearchGetDataNextPageSaga);
  yield takeEvery(KnmSearchActionsEnum.KNM_SEARCH_LOAD_FILE, knmSearchLoadFileSaga);
  yield takeEvery(
    KnmSearchActionsEnum.KNM_SEARCH_GET_KNM_SUPERVISION_TYPES_OPTIONS_ALL,
    knmSearchGetKnmSupervisionTypesAllOptionsSaga,
  );
  yield takeEvery(
    KnmSearchActionsEnum.KNM_SEARCH_GET_KNM_SUPERVISION_TYPES_ERVK_OPTIONS_ALL,
    knmSearchGetKnmSupervisionTypesErvkAllOptionsSaga,
  );
}

function* knmSearchInitSaga() {
  const state: IRootState = yield select();

  yield put(catalogGet(CommonCatalogsNamesEnum.KNM_YEARS));
  const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

  if (isErknm) {
    yield put(catalogGet(CommonCatalogsNamesEnum.KNM_STATUSES_248));
    yield put(catalogGet(CommonCatalogsNamesEnum.KNM_TYPES_CANCELED));
    yield put(catalogGet(CommonCatalogsNamesEnum.FEDERAL_LAWS_248));
    yield put(catalogKnmTypes248Get({}));
  } else {
    yield put(catalogGet(CommonCatalogsNamesEnum.KNM_STATUSES_294));
    yield put(catalogGet(CommonCatalogsNamesEnum.KNM_TYPES_294));
    yield put(catalogGet(CommonCatalogsNamesEnum.FEDERAL_LAWS_294));
  }

  yield put(catalogGet(CommonCatalogsNamesEnum.KNM_SUBJECT_TYPES));
  yield put(dictionariesKnmSubjectTypesGet());
}

function* knmSearchGetDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const openApiInfo = getCoreOpenApiInfo(state);
    const searchString = getKnmSearchSearchString(state);
    const order = getKnmSearchOrder(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const filter = knmSearchGetFilters(getKnmSearchFilter(state), isErknm);

    if (
      openApiInfo === undefined ||
      (!searchString && filter && !Object.values(filter).find(item => item.length > 0))
    ) {
      return;
    }
    const knmFilter = (filter || searchString
      ? { ...filter, ...(searchString && { searchString }) }
      : null) as IKnmSearchFilter;
    const data: IKnmSearchResponse = yield searchKNM(openApiInfo, 1, PAGE_SIZE, knmFilter, order);

    yield put(knmSearchSetData(data.list, data.page, data.pageCount, data.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmSearchGetDataNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const openApiInfo = getCoreOpenApiInfo(state);
    const searchString = getKnmSearchSearchString(state);
    const page = getKnmSearchPage(state);
    const order = getKnmSearchOrder(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const filter = knmSearchGetFilters(getKnmSearchFilter(state), isErknm);
    const knms = getKnmSearchKnms(state);

    if (
      openApiInfo === undefined ||
      (!searchString && filter && !Object.values(filter).find(item => item.length > 0))
    ) {
      return;
    }
    const knmFilter = (filter || searchString
      ? { ...filter, ...(searchString && { searchString }) }
      : null) as IKnmSearchFilter;
    const data: IKnmSearchResponse = yield searchKNM(openApiInfo, page + 1, PAGE_SIZE, knmFilter, order);

    yield put(knmSearchSetData([...knms, ...data.list], data.page, data.pageCount, data.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmSearchLoadFileSaga(action: ActionType<typeof knmSearchLoadFile>) {
  function* funcTry() {
    const { format } = action.payload;
    const state: IRootState = yield select();
    const openApiInfo = getCoreOpenApiInfo(state);
    const searchString = getKnmSearchSearchString(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const filter = knmSearchGetFilters(getKnmSearchFilter(state), isErknm);

    if (openApiInfo === undefined) {
      return;
    }
    const knmFilter = (filter || searchString
      ? { ...filter, ...(searchString && { searchString }) }
      : null) as IKnmSearchFilter;
    const link: string = yield exportKnm(openApiInfo, knmFilter, format);

    yield saveFile(link, `inspections.${format}`);
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmSearchGetKnmSupervisionTypesAllOptionsSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

    // Это старый метод получения старых данных (не из ЕРВК), для ЕРКНМ мы его больше не используем
    if (isErknm) {
      return;
    }

    const response: IKnmSupervisionType[] = yield fetchKnmSupervisionTypesAll();
    const supervisionOptions = response?.map<IKnmSupervisionTypeSelectOption>((type: IKnmSupervisionType) => ({
      ...type,
      id: type.id,
      label: type.name,
      value: type.code,
    }));

    yield put(knmSearchSetKnmSupervisionTypesOptionsAll(supervisionOptions));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmSearchGetKnmSupervisionTypesErvkAllOptionsSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

    const response: Array<ISupervisionType> = isErknm
      ? yield fetchKnm248SupervisionTypesErvkByFederalLaw()
      : yield fetchErpSupervisionTypesErvkByFederalLaw();

    const supervisionOptions: ISupervisionTypeSelectOption[] =
      response?.map(item => ({
        label: item.title,
        value: item.code,
        ...item,
      })) || [];

    yield put(knmSearchSetKnmSupervisionTypesErvkOptionsAll(supervisionOptions));
  }

  yield sagaWrapper({ funcTry });
}
